import Vue from 'vue';
import api from '@/api';
import eventHub from '@/eventHub';
import VeeValidate, { Validator } from 'vee-validate';
import VeeValidateConfig from '@/vee-validate-config';
import VueClipboard from 'vue-clipboard2'; // https://github.com/Inndy/vue-clipboard2
import message from 'shared-lib/helpers/message';
import LoadScript from 'vue-plugin-load-script';
import VueMq from 'vue-mq';
import breakpoints from '@/styles/settings/_singleFileComponentCommons.scss';

import ErrorLogger from 'shared-lib/helpers/errorLogger';
if (!process.env.VUE_APP_SKIP_ERROR_LOGGER) {
    new ErrorLogger({ onErrorFn: (errorLog) => api.errorLog(errorLog) });
}

// Global Mixins
import bemNameGenerator from 'shared-lib/mixins/bemNameGenerator';
// styles
import '@/styles/main.scss';

// Cherry pick element-ui
// **REMEMBER** to import appropriate styles in src/styles/element-ui.scss
import Slider from 'element-ui/lib/slider';
import DatePicker from 'element-ui/lib/date-picker';
import TimePicker from 'element-ui/lib/time-picker';
import TimeSelect from 'element-ui/lib/time-select';
import Loading from 'element-ui/lib/loading';
import Switch from 'element-ui/lib/switch';
import Popover from 'element-ui/lib/popover';
import Tag from 'element-ui/lib/tag';
import Select from 'element-ui/lib/select';
import Option from 'element-ui/lib/option';
import OptionGroup from 'element-ui/lib/option-group';
import Transfer from 'element-ui/lib/transfer';
import Alert from 'element-ui/lib/alert';
import Input from 'element-ui/lib/input';
import Autocomplete from 'element-ui/lib/autocomplete';
import Upload from 'element-ui/lib/upload';
import Steps from 'element-ui/lib/steps';
import Step from 'element-ui/lib/step';
import Collapse from 'element-ui/lib/collapse';
import CollapseItem from 'element-ui/lib/collapse-item';
import Notification from 'element-ui/lib/notification';
import Pagination from 'element-ui/lib/pagination';
import ElScrollbar from 'element-ui/lib/scrollbar';
import ElCheckbox from 'element-ui/lib/checkbox';
import ElCheckboxButton from 'element-ui/lib/checkbox-button';
import ElInputNumber from 'element-ui/lib/input-number';
import ElTree from 'element-ui/lib/tree';
import Button from 'element-ui/lib/button';
import Tooltip from 'element-ui/lib/tooltip';
import Dialog from 'element-ui/lib/dialog';
import Tabs from 'element-ui/lib/tabs';
import TabPane from 'element-ui/lib/tab-pane';
import Card from 'element-ui/lib/card';
import Dropdown from 'element-ui/lib/dropdown';
import DropdownMenu from 'element-ui/lib/dropdown-menu';
import DropdownItem from 'element-ui/lib/dropdown-item';
import RadioGroup from 'element-ui/lib/radio-group';
import Radio from 'element-ui/lib/radio';
import RadioButton from 'element-ui/lib/radio-button';
import CheckboxGroup from 'element-ui/lib/checkbox-group';
import Checkbox from 'element-ui/lib/checkbox';
import Drawer from 'element-ui/lib/drawer';
import Badge from 'element-ui/lib/badge';
import Menu from 'element-ui/lib/menu';
import MenuItem from 'element-ui/lib/menu-item';
import MenuItemGroup from 'element-ui/lib/menu-item-group';
import Submenu from 'element-ui/lib/submenu';
import Timeline from 'element-ui/lib/timeline';
import TimelineItem from 'element-ui/lib/timeline-item';


import(/* webpackChunkName: "icons-svg" */ '@/icons-svg');

// Internationalization
import { i18n } from '@/locale';
import ElementLocale from 'element-ui/lib/locale';
// configure element-ui language
ElementLocale.i18n((key, value) => i18n.t(key, value));

Vue.config.productionTip = false;

// allow clipboard in modals
VueClipboard.config.autoSetContainer = true;

// Global Event Hub
Vue.prototype.$eventHub = eventHub;

// Global API
Vue.prototype.$api = api;

// Global toasted messages
Vue.prototype.$message = message;
Vue.prototype.$notify = Notification;

// init validation
Vue.use(VeeValidate, VeeValidateConfig);
Validator.extend('isNotEmptyObject', (value) => {
    return typeof value === 'object' && Object.keys(value).length > 0;
});
Validator.extend('arrayOfValidEmails', {
    getMessage: () => 'All values must be valid emails',
    validate: emails => {
        for (let i in emails) {
            // eslint-disable-next-line max-len,no-useless-escape
            const validEmailRegExp = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

            if (!validEmailRegExp.test(emails[i])) {
                return false;
            }
        }

        return true;
    },
});
Validator.extend('offerCategories', {
    getMessage: () => 'An Offer can only have one category attached',
    validate: value => Array.isArray(value) ? value.length === 1 : !isNaN(value),
});
Validator.extend('max_time', {
    getMessage: field => 'The ' + field + ' value exceeds maximum time available.',
    validate: (value = '', [ valueMax = '' ]) => {
        const timeNumber = Number(value.replace(':', ''));
        const maxTimeNumber = Number(valueMax.replace(':', ''));
        return timeNumber <= maxTimeNumber;
    },
});
Validator.extend('min_time', {
    getMessage: field => 'The ' + field + ' value do not meet minimum time available.',
    validate: (value = '', [ valueMin = '' ]) => {
        const timeNumber = Number(value.replace(':', ''));
        const minTimeNumber = Number(valueMin.replace(':', ''));
        return timeNumber >= minTimeNumber;
    },
});
Validator.extend('notZero', {
    getMessage: () => 'Cannot be 0.',
    validate: (value) => {
        return Number(value) !== 0;
    },
});
Validator.extend('publisherAccessPasswordPolicy', {
    getMessage: () => 'Password must be a combination of 6-32 alphanumeric and special characters: .$#@!%^&*()_+=-',
    validate: (value) => /^(?=.*[a-zA-Z\d])(?=.*[.$#@!%^&*()_+=-])[A-Za-z\d.$#@!%^&*()_+=-]{6,32}$/.test(value),
});
Validator.extend('unique_params', {
    getMessage: () => 'Param already in use.',
    validate: (param, params) => {
        return params.filter(i => i === param).length < 2;
    },
});
Validator.extend('json', {
    getMessage: () => 'Must be valid JSON format.',
    validate: (value) => {
        if (!value || value.trim() === '') {
            return true; // Allow empty values
        }
        try {
            JSON.parse(value);
            return true;
        } catch (e) {
            return false;
        }
    },
});

// register plugins
Vue.use(VueClipboard);
Vue.use(Slider);
Vue.use(DatePicker);
Vue.use(TimePicker);
Vue.use(TimeSelect);
Vue.use(Loading);
Vue.use(Switch);
Vue.use(Popover);
Vue.use(Tag);
Vue.use(Select);
Vue.use(Option);
Vue.use(OptionGroup);
Vue.use(Transfer);
Vue.use(Alert);
Vue.use(Input);
Vue.use(Autocomplete);
Vue.use(Upload);
Vue.use(Steps);
Vue.use(Step);
Vue.use(Collapse);
Vue.use(CollapseItem);
Vue.use(Pagination);
Vue.use(ElScrollbar);
Vue.use(ElCheckbox);
Vue.use(ElCheckboxButton);
Vue.use(ElInputNumber);
Vue.use(ElTree);
Vue.use(Button);
Vue.use(Tooltip);
Vue.use(Dialog);
Vue.use(Tabs);
Vue.use(TabPane);
Vue.use(Card);
Vue.use(Dropdown);
Vue.use(DropdownMenu);
Vue.use(DropdownItem);
Vue.use(RadioGroup);
Vue.use(RadioButton);
Vue.use(Radio);
Vue.use(CheckboxGroup);
Vue.use(Checkbox);
Vue.use(Drawer);
Vue.use(Badge);
Vue.use(Menu);
Vue.use(MenuItem);
Vue.use(MenuItemGroup);
Vue.use(Submenu);
Vue.use(Timeline);
Vue.use(TimelineItem);

// register global mixins
Vue.mixin(bemNameGenerator);

Vue.use(VueMq, {
    breakpoints: {
        xs: parseInt(breakpoints.xs, 10),
        sm: parseInt(breakpoints.sm, 10),
        md: parseInt(breakpoints.md, 10),
        lg: parseInt(breakpoints.lg, 10),
        xl: parseInt(breakpoints.xl, 10),
    },
});

Vue.use(LoadScript);

// register global directives
import { clickOutside } from 'shared-lib/helpers/directives';
Vue.directive('clickOutside', clickOutside);

export { Vue, i18n };
